import React, { useState, useEffect, useRef, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import AlertBox from '../Components/AlertBox';
import Snackbar from '../Components/Snackbar';
import ConfirmDialog from '../Components/ConfirmDialog';

const UpdateStatus = ({ task, onClose, onStatusUpdate }) => {
  // State management
  const [taskData, setTaskData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [userRole, setUserRole] = useState(null);

  // Form states
  const [showInProgressForm, setShowInProgressForm] = useState(false);
  const [showCompleteForm, setShowCompleteForm] = useState(false);
  const [showReopenForm, setShowReopenForm] = useState(false);

  // Form data
  const [nextFollowUpDateTime, setNextFollowUpDateTime] = useState('');
  const [description, setDescription] = useState('');
  const [updating, setUpdating] = useState(false);

  // Attachment states
  const [attachments, setAttachments] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [accessPermissions, setAccessPermissions] = useState([]);

  // Refs for file inputs and audio handling
  const imageInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const audioChunksRef = useRef([]);

  const [showAlert, setShowAlert] = useState(null);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    type: 'info'
  });

  // Confirm Dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    type: 'warning',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    onConfirm: null
  });

  const [dialogLoading, setDialogLoading] = useState(false);

  // API configuration
  const API_BASE_URL = 'https://crm-tkxn.onrender.com/api';
  const authToken = localStorage.getItem('authToken')?.replace(/['"]/g, '').trim();

  // Enhanced getCompanyId function with JWT decoding
  const getCompanyId = useCallback(async () => {
    console.group('[DEBUG] getCompanyId() - Starting company ID resolution');

    const token = localStorage.getItem('authToken');
    console.log('📋 Token from localStorage:', token ? `${token.substring(0, 50)}...` : 'null');

    if (token) {
      try {
        console.log(' Attempting to decode JWT token...');
        const decodedToken = jwtDecode(token);
        console.log('📊 Decoded token structure:', JSON.stringify(decodedToken, null, 2));

        // New logic based on role
        if (decodedToken && decodedToken.role === 'company') {
          if (decodedToken.id) {
            console.groupEnd();
            return decodedToken.id;
          }
        } else {
          if (decodedToken && decodedToken.companyId) {
            console.groupEnd();
            return decodedToken.companyId;
          }
        }

        if (decodedToken && (decodedToken.userId || decodedToken.id)) {
          const userId = decodedToken.userId || decodedToken.id;
          console.log('Employee ID from JWT:', userId);
        } else {
          console.warn('⚠️ No userId or id found in JWT token');
        }
      } catch (error) {
        console.error('❌ JWT decoding failed:', error);
      }
    } else {
      console.warn('⚠️ No authToken found in localStorage');
    }

    // Fallback: try localStorage
    const storedCompanyId = localStorage.getItem('companyId');
    if (storedCompanyId) {
      console.log('✅ Found companyId in localStorage:', storedCompanyId);
      console.groupEnd();
      return storedCompanyId;
    } else {
      console.warn('⚠️ No companyId found in localStorage');
    }

    console.error('❌ Unable to resolve company ID from any source');
    console.groupEnd();
    return null;
  }, []);

  const hasPermission = (permission) => {
    // If user role is 'company', allow all permissions
    if (userRole === "company") {
      return true;
    }
    // Otherwise, check accessPermissions array
    return accessPermissions.includes(permission);
  };

  useEffect(() => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;
      const decoded = jwtDecode(token);
      setAccessPermissions(decoded.accessPermissions || []);
      setUserRole(decoded.role || null);
    } catch (error) {
      console.error("Failed to decode token:", error);
    }
  }, []);

  // Fetch all employees function
  const fetchEmployees = useCallback(async () => {
    try {
      const companyId = await getCompanyId();
      if (!companyId || !authToken) return;

      const response = await fetch(`${API_BASE_URL}/employees/company/${companyId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${authToken}`,
          'x-auth-token': authToken
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        const list = Array.isArray(data) ? data : (data.data || data.employees || []);
        setEmployees(list);
      }
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  }, [API_BASE_URL, authToken, getCompanyId]);

  // Fetch task details and status history
  useEffect(() => {
    if (task?._id) {
      fetchTaskDetails();
      fetchStatusHistory();
    }
  }, [task]);

  // Fetch company employees for name resolution
  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // Cleanup effect for audio
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      attachments.forEach(attachment => {
        if (attachment.url && attachment.url.startsWith('blob:')) {
          URL.revokeObjectURL(attachment.url);
        }
      });
    };
  }, [attachments]);

  // Fetch task details
  const fetchTaskDetails = async () => {
    try {
      setLoading(true);
      setTaskData(task);
      setLoading(false);
    } catch (error) {
      console.error('Error setting task data:', error);
      setError('Failed to load task data');
      setLoading(false);
    }
  };

  // Handle file upload
  const handleFileUpload = (e, type) => {
    const files = Array.from(e.target.files);
    const newAttachments = files.map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      type: type,
      size: file.size,
      file: file,
      url: URL.createObjectURL(file),
      uploadDate: new Date().toISOString()
    }));

    setAttachments(prev => [...prev, ...newAttachments]);
    e.target.value = '';
  };

  // Remove attachment
  const removeAttachment = (id) => {
    const attachment = attachments.find(att => att.id === id);
    if (attachment && attachment.url && attachment.url.startsWith('blob:')) {
      URL.revokeObjectURL(attachment.url);
    }
    setAttachments(attachments.filter(attachment => attachment.id !== id));
  };

  // Start audio recording
  const startRecording = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('MediaRecorder API is not supported in this browser');
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      });

      const options = MediaRecorder.isTypeSupported('audio/webm')
        ? { mimeType: 'audio/webm' }
        : {};

      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const mimeType = mediaRecorder.mimeType || 'audio/webm';
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        setAudioBlob(blob);

        const extension = mimeType.includes('webm') ? 'webm' : 'wav';
        const filename = `recording-${Date.now()}.${extension}`;

        const file = new File([blob], filename, { type: mimeType });

        const newAttachment = {
          id: Date.now() + Math.random(),
          name: file.name,
          type: 'audio',
          size: file.size,
          file: file,
          url: URL.createObjectURL(blob),
          uploadDate: new Date().toISOString()
        };

        setAttachments(prev => [...prev, newAttachment]);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event.error);
        setIsRecording(false);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start(1000);
      setIsRecording(true);

    } catch (error) {
      console.error('Error accessing microphone:', error);
      setIsRecording(false);
    }
  };

  // Stop audio recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      try {
        mediaRecorderRef.current.stop();
        setIsRecording(false);
      } catch (error) {
        console.error('Error stopping recording:', error);
        setIsRecording(false);
      }
    }
  };

  // Audio playback functions
  const playAudio = (url) => {
    if (audioRef.current) {
      audioRef.current.src = url;
      audioRef.current.play().catch(error => {
        console.error('Error playing audio:', error);
      });
      setIsPlaying(true);

      progressIntervalRef.current = setInterval(() => {
        if (audioRef.current) {
          setCurrentTime(audioRef.current.currentTime);
          setDuration(audioRef.current.duration || 0);

          if (audioRef.current.ended) {
            setIsPlaying(false);
            setCurrentTime(0);
            clearInterval(progressIntervalRef.current);
          }
        }
      }, 100);
    }
  };

  const pauseAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      clearInterval(progressIntervalRef.current);
    }
  };

  // Utility functions
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const getFileIcon = (type) => {
    const iconClass = "w-5 h-5 text-white";
    switch (type) {
      case 'image':
        return (
          <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
            <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        );
      case 'audio':
        return (
          <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
            <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="w-8 h-8 bg-gray-500 rounded-lg flex items-center justify-center">
            <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
        );
    }
  };

  // Fetch status history
  const fetchStatusHistory = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/taskstatusupdate/${task._id}/status-updates`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
          'x-auth-token': authToken
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      let historyData = [];

      if (Array.isArray(data)) {
        historyData = data;
      } else if (data.data && Array.isArray(data.data)) {
        historyData = data.data;
      } else if (data.history && Array.isArray(data.history)) {
        historyData = data.history;
      } else if (data.statusUpdates && Array.isArray(data.statusUpdates)) {
        historyData = data.statusUpdates;
      } else if (data.updates && Array.isArray(data.updates)) {
        historyData = data.updates;
      }

      // Filter to only include updates for the current task._id
      historyData = historyData.filter(update => {
        const isForCurrentTask = update.taskId === task._id || update.task === task._id;
        const isNotReassigned = !(update.status && update.status.toLowerCase() === 'reassigned');
        return isForCurrentTask && isNotReassigned;
      });

      // Sort history by date (newest first)
      const sortedHistory = historyData.sort((a, b) => {
        const dateA = new Date(a.updatedAt || a.createdAt || a.timestamp);
        const dateB = new Date(b.updatedAt || b.createdAt || b.timestamp);
        return dateB - dateA;
      });

      setStatusHistory(sortedHistory);

    } catch (error) {
      console.error('Error fetching status history:', error);
      setStatusHistory([]);
    } finally {
      setLoading(false);
    }
  };


  const updateTaskStatus = async (newStatus, additionalData = {}) => {
    try {
      setUpdating(true);

      const hasAttachments = attachments.length > 0;
      let result;

      if (hasAttachments) {
        const formData = new FormData();
        formData.append('status', newStatus);
        formData.append('description', description);

        if (nextFollowUpDateTime) {
          const followUpDate = new Date(nextFollowUpDateTime).toISOString();
          formData.append('nextFollowUpDateTime', followUpDate);
        }

        Object.keys(additionalData).forEach(key => {
          if (key === 'nextFollowUpDateTime' && nextFollowUpDateTime) {
            return;
          }
          formData.append(key, additionalData[key]);
        });

        attachments.forEach((attachment) => {
          // Only send one file per type, using backend field names
          if (attachment.file) {
            if (attachment.type === 'image' && !formData.has('image')) {
              formData.append('image', attachment.file);
            } else if (attachment.type === 'file' && !formData.has('file')) {
              formData.append('file', attachment.file);
            } else if (attachment.type === 'audio' && !formData.has('audio')) {
              formData.append('audio', attachment.file);
            }
          }
        });
        console.log("formData", formData);


        const response = await fetch(`${API_BASE_URL}/taskstatusupdate/${task._id}/status`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'x-auth-token': authToken,
          },
          credentials: 'include',
          body: formData
        });

        if (!response.ok) {
          throw new Error('Failed to update status with attachments');
        }

        result = await response.json();
      } else {
        const requestBody = {
          status: newStatus,
          description: description,
          ...additionalData
        };

        if (nextFollowUpDateTime) {
          const followUpDate = new Date(nextFollowUpDateTime).toISOString();
          requestBody.nextFollowUpDateTime = followUpDate;
        }
        console.log("requestBody", requestBody);

        const response = await fetch(`${API_BASE_URL}/taskstatusupdate/${task._id}/status`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
            'x-auth-token': authToken
          },
          credentials: 'include',
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          throw new Error('Failed to update status');
        }

        result = await response.json();
      }

      // Immediate UI update - update local state first
      setTaskData(prev => ({
        ...prev,
        status: newStatus,
        nextFollowUpDateTime: nextFollowUpDateTime || prev.nextFollowUpDateTime
      }));

      // Get friendly status name for message
      const statusDisplayName = newStatus
        .replace(/-/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase());

      // Show success notification
      setSnackbar({
        open: true,
        message: `Task marked as "${statusDisplayName}" successfully!`,
        type: 'success'
      });

      // Reset form states after a short delay to let user see the success message
      setTimeout(() => {
        resetFormStates();
      }, 500);

      // Refresh history in background
      fetchStatusHistory().catch(console.error);

      // Global event for other components (Analytics, etc.)
      window.dispatchEvent(new CustomEvent('taskUpdated', { detail: result }));

      // Notify parent component with the updated data
      if (onStatusUpdate) {
        onStatusUpdate({
          ...result,
          updatedTask: {
            ...taskData,
            status: newStatus,
            nextFollowUpDateTime: nextFollowUpDateTime || taskData?.nextFollowUpDateTime
          }
        });
      }

    } catch (error) {
      console.error('Error updating status:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update task status',
        type: 'error'
      });
    } finally {
      setUpdating(false);
    }
  };


  const resetFormStates = () => {
    setShowInProgressForm(false);
    setShowCompleteForm(false);
    setShowReopenForm(false);
    setNextFollowUpDateTime('');
    setDescription('');

    // Clean up attachments and URLs
    attachments.forEach(attachment => {
      if (attachment.url && attachment.url.startsWith('blob:')) {
        URL.revokeObjectURL(attachment.url);
      }
    });
    setAttachments([]);
    setAudioBlob(null);

    // Reset audio state
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);

    // Stop recording if active
    if (isRecording) {
      stopRecording();
    }
  };


  // Get current status with immediate updates
  const getCurrentStatus = () => {
    return taskData?.status || 'pending';
  };

  // Enhanced status color function
  const getStatusColor = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800';

    const statusLower = status.toLowerCase();
    if (statusLower.includes('complete') || statusLower.includes('completed')) {
      return statusLower.includes('late') ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800';
    }
    if (statusLower.includes('progress')) return 'bg-blue-100 text-blue-800';
    if (statusLower.includes('pending') || statusLower.includes('reopened')) return 'bg-yellow-100 text-yellow-800';
    return 'bg-gray-100 text-gray-800';
  };

  // Enhanced overdue check
  const isOverdue = () => {
    if (!taskData?.endDateTime) return false;
    const endDate = new Date(taskData.endDateTime);
    const now = new Date();
    const isCompleted = (taskData.status || '').toLowerCase().includes('complete');
    return now > endDate && !isCompleted;
  };

  // Get appropriate status based on current status and overdue condition
  const getNewStatus = (baseStatus) => {
    const isReopenedTask = taskData?.status?.startsWith('re-') || taskData?.status === 'reopened';
    const overdue = isOverdue();

    if (baseStatus === 'completed') {
      if (overdue) {
        return isReopenedTask ? 're-late-complete' : 'late-complete';
      }
      return isReopenedTask ? 're-complete' : 'completed';
    }

    if (baseStatus === 'in-progress') {
      if (overdue) {
        return isReopenedTask ? 're-in-progress(Overdue)' : 'in-progress(Overdue)';
      }
      return isReopenedTask ? 're-in-progress' : 'in-progress';
    }

    if (baseStatus === 'pending') {
      return overdue ? 'reopened(Overdue)' : 'reopened';
    }

    return baseStatus;
  };

  // Handle status button clicks
  const handleInProgressClick = () => {
    setShowInProgressForm(true);
    setShowCompleteForm(false);
    setShowReopenForm(false);
  };

  const handleCompleteClick = () => {
    setShowCompleteForm(true);
    setShowInProgressForm(false);
    setShowReopenForm(false);
  };

  const handleReopenClick = () => {
    setShowReopenForm(true);
    setShowInProgressForm(false);
    setShowCompleteForm(false);
  };

  // Handle form submissions
  const handleInProgressSubmit = () => {
    if (!nextFollowUpDateTime || !description) {
      setSnackbar({
        open: true,
        message: 'Next follow-up date and description are required',
        type: 'error'
      });
      return;
    }

    const newStatus = getNewStatus('in-progress');
    updateTaskStatus(newStatus, { nextFollowUpDateTime: nextFollowUpDateTime });
  };

  const handleCompleteSubmit = () => {
    if (!description) {
      setSnackbar({
        open: true,
        message: 'Description is required to mark task as complete',
        type: 'error'
      });
      return;
    }

    setConfirmDialog({
      open: true,
      title: 'Mark as Complete',
      message: 'Are you sure you want to mark this task as complete? This action will update the task status.',
      type: 'success',
      confirmText: 'Mark Complete',
      cancelText: 'Cancel',
      onConfirm: () => {
        const newStatus = getNewStatus('completed');
        updateTaskStatus(newStatus);
        setConfirmDialog({ ...confirmDialog, open: false });
      }
    });
  };

  const handleReopenSubmit = () => {
    if (!nextFollowUpDateTime || !description) {
      setSnackbar({
        open: true,
        message: 'Next follow-up date and description are required to reopen task',
        type: 'error'
      });
      return;
    }

    updateTaskStatus('reopened', { nextFollowUpDateTime: nextFollowUpDateTime });
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  // Get employee name
  const isObjectId = (val) => typeof val === 'string' && /^[a-fA-F0-9]{24}$/.test(val.trim());

  const getEmployeeName = useCallback((employeeRef) => {
    const id = typeof employeeRef === 'string'
      ? (isObjectId(employeeRef) ? employeeRef : null)
      : employeeRef?._id || employeeRef?.id || null;

    if (id) {
      const emp = employees.find(e => String(e._id) === String(id));
      if (emp) {
        return emp.teamMemberName || `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.name || 'Unknown Employee';
      }
    }

    if (typeof employeeRef === 'object' && employeeRef) {
      return employeeRef.teamMemberName || `${employeeRef.firstName || ''} ${employeeRef.lastName || ''}`.trim() || employeeRef.name || 'Unknown Employee';
    }
    if (typeof employeeRef === 'string') return employeeRef;
    return 'Unknown Employee';
  }, [employees]);


  const shouldShowStatusButtons = () => {
    const status = getCurrentStatus().toLowerCase();
    return !status.includes('complete') && !status.includes('completed');
  };

  // Check if reopen button should be shown
  const shouldShowReopenButton = () => {
    const status = getCurrentStatus().toLowerCase();
    return status.includes('complete') || status.includes('completed');
  };


  // Helper functions for table attachments
  const getAttachmentsByType = (attachments, type) => {
    if (!attachments || !Array.isArray(attachments)) return [];
    return attachments.filter(att => att.type === type || att.mimeType?.includes(type));
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl p-8 text-center shadow-2xl" style={{ background: '#FFFFFF' }}>
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-700" style={{ color: '#2C2C2C' }}>Loading task details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl p-8 max-w-md shadow-2xl" style={{ background: '#FFFFFF' }}>
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2" style={{ color: '#2C2C2C' }}>Error</h3>
            <p className="text-gray-600 mb-6" style={{ color: '#6E6E6E' }}>{error}</p>
            <button
              onClick={onClose}
              className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-100 p-4" style={{ zIndex: '99999' }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto" style={{ background: '#F8F9FA' }}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-6 z-10 rounded-t-2xl" style={{ background: '#FFFFFF', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center mr-4">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900" style={{ color: '#2C2C2C' }}>Update Task Status</h2>
                <p className="text-gray-500 text-sm" style={{ color: '#6E6E6E' }}>Manage task progress and status updates</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
              style={{ background: '#F5F6FA' }}
              disabled={updating}
            >
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-8">
          {/* Task Information */}
          <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200" style={{ background: '#FFFFFF', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center" style={{ color: '#2C2C2C' }}>
              <svg className="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Task Information
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" style={{ color: '#2C2C2C' }}>Task Name</label>
                <p className="text-gray-900 p-3 rounded-lg border border-gray-200" style={{ background: '#F8F9FA', color: '#2C2C2C' }}>{taskData?.title || 'N/A'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" style={{ color: '#2C2C2C' }}>Priority</label>
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${taskData?.priority === 'high' ? 'bg-red-100 text-red-800' :
                  taskData?.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                  {taskData?.priority || 'Medium'}
                </span>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2" style={{ color: '#2C2C2C' }}>Description</label>
                <p className="text-gray-900 p-3 rounded-lg border border-gray-200 min-h-[80px]" style={{ background: '#F8F9FA', color: '#2C2C2C' }}>
                  {taskData?.description || 'No description provided'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" style={{ color: '#2C2C2C' }}>Start Date</label>
                <p className="text-gray-900 p-3 rounded-lg border border-gray-200" style={{ background: '#F8F9FA', color: '#2C2C2C' }}>{formatDate(taskData?.startDateTime)}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" style={{ color: '#2C2C2C' }}>End Date</label>
                <p className="text-gray-900 p-3 rounded-lg border border-gray-200" style={{ background: '#F8F9FA', color: '#2C2C2C' }}>{formatDate(taskData?.endDateTime)}</p>
              </div>

              {taskData?.nextFollowUpDateTime && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2" style={{ color: '#2C2C2C' }}>Current Next Follow-up Date</label>
                  <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                    <p className="text-blue-800 font-medium">{formatDate(taskData.nextFollowUpDateTime)}</p>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" style={{ color: '#2C2C2C' }}>Assigned To</label>
                <div className="flex flex-wrap gap-2">
                  {taskData?.assignedTo ? (
                    Array.isArray(taskData.assignedTo) ?
                      taskData.assignedTo.map((emp, index) => (
                        <span key={index} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                          {getEmployeeName(emp)}
                        </span>
                      )) :
                      <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                        {getEmployeeName(taskData.assignedTo)}
                      </span>
                  ) : (
                    <span className="text-gray-500">Not assigned</span>
                  )}
                </div>
              </div>

              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" style={{ color: '#2C2C2C' }}>Current Status</label>
                <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(taskData?.status)}`}>
                  {taskData?.status || 'pending'}
                  {isOverdue() && !taskData?.status?.includes('complete') && !(taskData?.status || '').toLowerCase().includes('(overdue)') && (
                    <span className="ml-2 text-red-600 font-bold">(Overdue)</span>
                  )}
                </span>
              </div> */}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" style={{ color: '#2C2C2C' }}>Current Status</label>
                <div className="flex items-center gap-2">
                  <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(getCurrentStatus())}`}>
                    {getCurrentStatus() || 'pending'}
                    {isOverdue() && !getCurrentStatus()?.toLowerCase().includes('complete') && !getCurrentStatus()?.toLowerCase().includes('(overdue)') && (
                      <span className="ml-2 text-red-600 font-bold">(Overdue)</span>
                    )}
                  </span>
                  {updating && (
                    <div className="flex items-center text-blue-600 text-sm">
                      <svg className="animate-spin h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Updating...
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Status Update Buttons */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center" style={{ color: '#2C2C2C' }}>
              <svg className="w-5 h-5 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Update Status
            </h3>

            {shouldShowStatusButtons() && (
              <div className="flex flex-wrap gap-4 mb-6">
                <button
                  onClick={handleInProgressClick}
                  disabled={updating}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Mark In Progress
                </button>
                <button
                  onClick={handleCompleteClick}
                  disabled={updating}
                  className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Mark Complete
                </button>
              </div>
            )}

            {shouldShowReopenButton() && (
              <div className="flex flex-wrap gap-4 mb-6">
                {hasPermission("update_task") && (
                  <button
                    onClick={handleReopenClick}
                    disabled={updating}
                    className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Re-open Task
                  </button>
                )}
              </div>
            )}
          </div>

          {/* In Progress Form */}
          {showInProgressForm && (
            <div className="bg-white rounded-xl p-6 mb-6 border border-blue-200" style={{ background: '#FFFFFF', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center" style={{ color: '#2C2C2C' }}>
                <div className="w-6 h-6 bg-blue-500 rounded-lg flex items-center justify-center mr-2">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                Mark as In Progress
              </h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2" style={{ color: '#2C2C2C' }}>Next Follow-up Date & Time *</label>
                  <input
                    type="datetime-local"
                    value={nextFollowUpDateTime}
                    onChange={(e) => setNextFollowUpDateTime(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    style={{ background: '#FFFFFF', borderColor: '#E2E8F0' }}
                    min={new Date().toISOString().slice(0, 16)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2" style={{ color: '#2C2C2C' }}>Description *</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe the progress or current status..."
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-colors"
                    style={{ background: '#FFFFFF', borderColor: '#E2E8F0' }}
                    rows="4"
                  />
                </div>

                {/* Attachments Section */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200" style={{ background: '#F5F6FA' }}>
                  <h5 className="text-md font-medium text-gray-900 mb-3 flex items-center" style={{ color: '#2C2C2C' }}>
                    <svg className="w-4 h-4 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.586-6.586a4 4 0 00-5.656-5.656l-6.586 6.586a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                    Attachments (Optional)
                  </h5>

                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <input type="file" ref={imageInputRef} onChange={(e) => handleFileUpload(e, 'image')} accept="image/*" multiple className="hidden" />
                    <input type="file" ref={fileInputRef} onChange={(e) => handleFileUpload(e, 'file')} multiple className="hidden" />
                    <input type="file" ref={audioInputRef} onChange={(e) => handleFileUpload(e, 'audio')} accept="audio/*" multiple className="hidden" />

                    <button
                      type="button"
                      onClick={() => imageInputRef.current && imageInputRef.current.click()}
                      className="flex flex-col items-center p-4 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-xl transition-all duration-200 group"
                      style={{ background: '#EFF6FF', borderColor: '#BFDBFE' }}
                    >
                      <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <span className="text-sm font-semibold text-blue-700">Images</span>
                      <span className="text-xs text-blue-600 mt-1">Max 2 MB</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => fileInputRef.current && fileInputRef.current.click()}
                      className="flex flex-col items-center p-4 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-xl transition-all duration-200 group"
                      style={{ background: '#FAF5FF', borderColor: '#E9D8FD' }}
                    >
                      <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <span className="text-sm font-semibold text-purple-700">Files</span>
                      <span className="text-xs text-purple-600 mt-1">Max 2 MB</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => audioInputRef.current && audioInputRef.current.click()}
                      className="flex flex-col items-center p-4 bg-green-50 hover:bg-green-100 border border-green-200 rounded-xl transition-all duration-200 group"
                      style={{ background: '#F0FDF4', borderColor: '#BBF7D0' }}
                    >
                      <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                        </svg>
                      </div>
                      <span className="text-sm font-semibold text-green-700">Audio Files</span>
                      <span className="text-xs text-green-600 mt-1">Max 2 MB</span>
                    </button>
                  </div>

                  {attachments.length > 0 && (
                    <div className="bg-white rounded-lg p-4 border border-gray-200" style={{ background: '#FFFFFF' }}>
                      <h6 className="font-medium text-gray-900 mb-3 flex items-center text-sm" style={{ color: '#2C2C2C' }}>
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                        Attached Files ({attachments.length})
                      </h6>
                      <div className="space-y-2">
                        {attachments.map(attachment => (
                          <div key={attachment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200" style={{ background: '#F8F9FA' }}>
                            <div className="flex items-center flex-1">
                              <div className="w-8 h-8 bg-gray-400 rounded-lg flex items-center justify-center mr-3">
                                {getFileIcon(attachment.type)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 truncate text-sm" style={{ color: '#2C2C2C' }}>{attachment.name}</p>
                                <p className="text-xs text-gray-500" style={{ color: '#6E6E6E' }}>
                                  {formatFileSize(attachment.size)} • {new Date(attachment.uploadDate).toLocaleDateString()}
                                </p>
                              </div>
                            </div>

                            {attachment.type === 'audio' && (
                              <div className="flex items-center mx-3 space-x-2">
                                <button
                                  type="button"
                                  onClick={() => isPlaying ? pauseAudio() : playAudio(attachment.url)}
                                  className="w-8 h-8 bg-blue-500 hover:bg-blue-600 rounded-lg flex items-center justify-center text-white transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isPlaying ? "M10 9v6m4-6v6" : "M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"} />
                                  </svg>
                                </button>
                                <div className="text-xs text-gray-500 min-w-0" style={{ color: '#6E6E6E' }}>
                                  <div>{formatTime(currentTime)} / {formatTime(duration)}</div>
                                </div>
                              </div>
                            )}

                            <button
                              type="button"
                              onClick={() => removeAttachment(attachment.id)}
                              className="w-8 h-8 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg flex items-center justify-center transition-colors"
                              style={{ background: '#FEF2F2' }}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleInProgressSubmit}
                    disabled={updating || !nextFollowUpDateTime || !description.trim()}
                    className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {updating ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Updating...
                      </>
                    ) : (
                      'Update Status'
                    )}
                  </button>
                  <button
                    onClick={() => setShowInProgressForm(false)}
                    disabled={updating}
                    className="px-6 py-2.5 bg-gray-300 hover:bg-gray-400 text-gray-700 font-semibold rounded-lg transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Complete Form */}
          {showCompleteForm && (
            <div className="bg-white rounded-xl p-6 mb-6 border border-green-200" style={{ background: '#FFFFFF', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center" style={{ color: '#2C2C2C' }}>
                <div className="w-6 h-6 bg-green-500 rounded-lg flex items-center justify-center mr-2">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                Mark as Complete
              </h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2" style={{ color: '#2C2C2C' }}>Completion Description *</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe how the task was completed..."
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none transition-colors"
                    style={{ background: '#FFFFFF', borderColor: '#E2E8F0' }}
                    rows="4"
                  />
                </div>

                {/* Attachments Section - Only Images and Files, Record removed */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200" style={{ background: '#F5F6FA' }}>
                  <h5 className="text-md font-medium text-gray-900 mb-3 flex items-center" style={{ color: '#2C2C2C' }}>
                    <svg className="w-4 h-4 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.586-6.586a4 4 0 00-5.656-5.656l-6.586 6.586a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                    Attachments (Optional)
                  </h5>
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <input type="file" ref={imageInputRef} onChange={(e) => handleFileUpload(e, 'image')} accept="image/*" multiple className="hidden" />
                    <input type="file" ref={fileInputRef} onChange={(e) => handleFileUpload(e, 'file')} multiple className="hidden" />
                    <input type="file" ref={audioInputRef} onChange={(e) => handleFileUpload(e, 'audio')} accept="audio/*" multiple className="hidden" />

                    <button
                      type="button"
                      onClick={() => imageInputRef.current && imageInputRef.current.click()}
                      className="flex flex-col items-center p-4 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-xl transition-all duration-200 group"
                      style={{ background: '#EFF6FF', borderColor: '#BFDBFE' }}
                    >
                      <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <span className="text-sm font-semibold text-blue-700">Images</span>
                      <span className="text-xs text-blue-600 mt-1">Max 2 MB</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => fileInputRef.current && fileInputRef.current.click()}
                      className="flex flex-col items-center p-4 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-xl transition-all duration-200 group"
                      style={{ background: '#FAF5FF', borderColor: '#E9D8FD' }}
                    >
                      <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <span className="text-sm font-semibold text-purple-700">Files</span>
                      <span className="text-xs text-purple-600 mt-1">Max 2 MB</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => audioInputRef.current && audioInputRef.current.click()}
                      className="flex flex-col items-center p-4 bg-green-50 hover:bg-green-100 border border-green-200 rounded-xl transition-all duration-200 group"
                      style={{ background: '#F0FDF4', borderColor: '#BBF7D0' }}
                    >
                      <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                        </svg>
                      </div>
                      <span className="text-sm font-semibold text-green-700">Audio Files</span>
                      <span className="text-xs text-green-600 mt-1">Max 2 MB</span>
                    </button>
                  </div>

                  {attachments.length > 0 && (
                    <div className="bg-white rounded-lg p-4 border border-gray-200" style={{ background: '#FFFFFF' }}>
                      <h6 className="font-medium text-gray-900 mb-3 flex items-center text-sm" style={{ color: '#2C2C2C' }}>
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                        Attached Files ({attachments.length})
                      </h6>
                      <div className="space-y-2">
                        {attachments.map(attachment => (
                          <div key={attachment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200" style={{ background: '#F8F9FA' }}>
                            <div className="flex items-center flex-1">
                              <div className="w-8 h-8 bg-gray-400 rounded-lg flex items-center justify-center mr-3">
                                {getFileIcon(attachment.type)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 truncate text-sm" style={{ color: '#2C2C2C' }}>{attachment.name}</p>
                                <p className="text-xs text-gray-500" style={{ color: '#6E6E6E' }}>
                                  {formatFileSize(attachment.size)} • {new Date(attachment.uploadDate).toLocaleDateString()}
                                </p>
                              </div>
                            </div>

                            {attachment.type === 'audio' && (
                              <div className="flex items-center mx-3 space-x-2">
                                <button
                                  type="button"
                                  onClick={() => isPlaying ? pauseAudio() : playAudio(attachment.url)}
                                  className="w-8 h-8 bg-blue-500 hover:bg-blue-600 rounded-lg flex items-center justify-center text-white transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isPlaying ? "M10 9v6m4-6v6" : "M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"} />
                                  </svg>
                                </button>
                                <div className="text-xs text-gray-500 min-w-0" style={{ color: '#6E6E6E' }}>
                                  <div>{formatTime(currentTime)} / {formatTime(duration)}</div>
                                </div>
                              </div>
                            )}

                            <button
                              type="button"
                              onClick={() => removeAttachment(attachment.id)}
                              className="w-8 h-8 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg flex items-center justify-center transition-colors"
                              style={{ background: '#FEF2F2' }}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleCompleteSubmit}
                    disabled={updating || !description.trim()}
                    className="px-6 py-2.5 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {updating ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Updating...
                      </>
                    ) : (
                      'Update Status'
                    )}
                  </button>
                  <button
                    onClick={() => setShowCompleteForm(false)}
                    disabled={updating}
                    className="px-6 py-2.5 bg-gray-300 hover:bg-gray-400 text-gray-700 font-semibold rounded-lg transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Reopen Form */}
          {showReopenForm && (
            <div className="bg-white rounded-xl p-6 mb-6 border border-purple-200" style={{ background: '#FFFFFF', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center" style={{ color: '#2C2C2C' }}>
                <div className="w-6 h-6 bg-purple-500 rounded-lg flex items-center justify-center mr-2">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
                Re-open Task
              </h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2" style={{ color: '#2C2C2C' }}>Next Follow-up Date & Time *</label>
                  <input
                    type="datetime-local"
                    value={nextFollowUpDateTime}
                    onChange={(e) => setNextFollowUpDateTime(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                    style={{ background: '#FFFFFF', borderColor: '#E2E8F0' }}
                    min={new Date().toISOString().slice(0, 16)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2" style={{ color: '#2C2C2C' }}>Reason for Reopening *</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Explain why the task needs to be reopened..."
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none transition-colors"
                    style={{ background: '#FFFFFF', borderColor: '#E2E8F0' }}
                    rows="4"
                  />
                </div>

                {/* Attachments Section - Same structure as other forms */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200" style={{ background: '#F5F6FA' }}>
                  <h5 className="text-md font-medium text-gray-900 mb-3 flex items-center" style={{ color: '#2C2C2C' }}>
                    <svg className="w-4 h-4 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.586-6.586a4 4 0 00-5.656-5.656l-6.586 6.586a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                    Attachments (Optional)
                  </h5>

                  {/* Hidden file inputs */}
                  <input type="file" ref={imageInputRef} onChange={(e) => handleFileUpload(e, 'image')} accept="image/*" multiple className="hidden" />
                  <input type="file" ref={fileInputRef} onChange={(e) => handleFileUpload(e, 'file')} multiple className="hidden" />
                  <input type="file" ref={audioInputRef} onChange={(e) => handleFileUpload(e, 'audio')} accept="audio/*" multiple className="hidden" />

                  {/* Same attachment UI as other forms */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <button
                      type="button"
                      onClick={() => imageInputRef.current && imageInputRef.current.click()}
                      className="flex flex-col items-center p-4 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-xl transition-all duration-200 group"
                      style={{ background: '#EFF6FF', borderColor: '#BFDBFE' }}
                    >
                      <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <span className="text-sm font-semibold text-blue-700">Images</span>
                      <span className="text-xs text-blue-600 mt-1">Max 2 MB</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => fileInputRef.current && fileInputRef.current.click()}
                      className="flex flex-col items-center p-4 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-xl transition-all duration-200 group"
                      style={{ background: '#FAF5FF', borderColor: '#E9D8FD' }}
                    >
                      <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <span className="text-sm font-semibold text-purple-700">Files</span>
                      <span className="text-xs text-purple-600 mt-1">Max 2 MB</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => audioInputRef.current && audioInputRef.current.click()}
                      className="flex flex-col items-center p-4 bg-green-50 hover:bg-green-100 border border-green-200 rounded-xl transition-all duration-200 group"
                      style={{ background: '#F0FDF4', borderColor: '#BBF7D0' }}
                    >
                      <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                        </svg>
                      </div>
                      <span className="text-sm font-semibold text-green-700">Audio Files</span>
                      <span className="text-xs text-green-600 mt-1">Max 2 MB</span>
                    </button>
                  </div>

                  {attachments.length > 0 && (
                    <div className="bg-white rounded-lg p-4 border border-gray-200" style={{ background: '#FFFFFF' }}>
                      <h6 className="font-medium text-gray-900 mb-3 flex items-center text-sm" style={{ color: '#2C2C2C' }}>
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                        Attached Files ({attachments.length})
                      </h6>
                      <div className="space-y-2">
                        {attachments.map(attachment => (
                          <div key={attachment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200" style={{ background: '#F8F9FA' }}>
                            <div className="flex items-center flex-1">
                              <div className="w-8 h-8 bg-gray-400 rounded-lg flex items-center justify-center mr-3">
                                {getFileIcon(attachment.type)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 truncate text-sm" style={{ color: '#2C2C2C' }}>{attachment.name}</p>
                                <p className="text-xs text-gray-500" style={{ color: '#6E6E6E' }}>
                                  {formatFileSize(attachment.size)} • {new Date(attachment.uploadDate).toLocaleDateString()}
                                </p>
                              </div>
                            </div>

                            {attachment.type === 'audio' && (
                              <div className="flex items-center mx-3 space-x-2">
                                <button
                                  type="button"
                                  onClick={() => isPlaying ? pauseAudio() : playAudio(attachment.url)}
                                  className="w-8 h-8 bg-blue-500 hover:bg-blue-600 rounded-lg flex items-center justify-center text-white transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isPlaying ? "M10 9v6m4-6v6" : "M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"} />
                                  </svg>
                                </button>
                                <div className="text-xs text-gray-500 min-w-0" style={{ color: '#6E6E6E' }}>
                                  <div>{formatTime(currentTime)} / {formatTime(duration)}</div>
                                </div>
                              </div>
                            )}

                            <button
                              type="button"
                              onClick={() => removeAttachment(attachment.id)}
                              className="w-8 h-8 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg flex items-center justify-center transition-colors"
                              style={{ background: '#FEF2F2' }}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex gap-3">

                  <button
                    onClick={handleReopenSubmit}
                    disabled={updating || !nextFollowUpDateTime || !description.trim()}
                    className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {updating ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Updating...
                      </>
                    ) : (
                      'Update Status'
                    )}
                  </button>
                  <button
                    onClick={() => setShowReopenForm(false)}
                    disabled={updating}
                    className="px-6 py-2.5 bg-gray-300 hover:bg-gray-400 text-gray-700 font-semibold rounded-lg transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Status History Section */}
          <div>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center" style={{ color: '#2C2C2C' }}>
                <svg className="w-5 h-5 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Status History
              </h3>
              <button
                onClick={fetchStatusHistory}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    Loading...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh
                  </>
                )}
              </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" style={{ background: '#FFFFFF' }}>
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
                  <p className="text-gray-500" style={{ color: '#6E6E6E' }}>Loading status history...</p>
                </div>
              ) : statusHistory.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200" style={{ background: '#F5F6FA' }}>
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ color: '#6E6E6E' }}>
                          Task
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ color: '#6E6E6E' }}>
                          Status
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ color: '#6E6E6E' }}>
                          Description
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ color: '#6E6E6E' }}>
                          Next Follow-up
                        </th>
                        <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ color: '#6E6E6E' }}>
                          Attachments
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200" style={{ background: '#FFFFFF' }}>
                      {statusHistory.map((history, index) => {
                        // Build attachments from backend fields

                        // Helper to get full URL for attachments
                        const getFullUrl = (url) => {
                          if (!url) return '';
                          if (url.startsWith('http')) return url;
                          return `${API_BASE_URL}/${url.replace(/^\/+/, '')}`;
                        };

                        const imageAttachments = history.image ? [{ url: getFullUrl(history.image), type: 'image' }] : [];
                        const fileAttachments = history.file ? [{ url: getFullUrl(history.file), type: 'file' }] : [];
                        const audioAttachments = history.audio ? [{ url: getFullUrl(history.audio), type: 'audio' }] : [];

                        return (
                          <tr key={history._id || index} className="hover:bg-gray-50 transition-colors" style={{ background: '#FFFFFF' }}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900" style={{ color: '#2C2C2C' }}>
                                {truncateText(taskData?.title || 'Unknown Task', 30)}
                              </div>
                              <div className="text-xs text-gray-500" style={{ color: '#6E6E6E' }}>
                                {formatDate(history.updatedAt || history.createdAt || history.timestamp)}
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(history.status)}`}>
                                {history.status || 'Unknown'}
                              </span>
                            </td>

                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900 max-w-xs" style={{ color: '#2C2C2C' }}>
                                {history.description ? (
                                  <div>
                                    <p className="truncate" title={history.description}>
                                      {truncateText(history.description, 100)}
                                    </p>
                                    {history.description.length > 100 && (
                                      <button
                                        className="text-blue-600 text-xs hover:underline mt-1"
                                        onClick={() => setShowAlert(history.description)}
                                      >
                                        View full
                                      </button>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-gray-400 italic">No description</span>
                                )}
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900" style={{ color: '#2C2C2C' }}>
                                {history.nextFollowUpDateTime ? (
                                  <div className="bg-blue-50 px-2 py-1 rounded text-blue-800 text-xs">
                                    {formatDate(history.nextFollowUpDateTime)}
                                  </div>
                                ) : (
                                  <span className="text-gray-400 text-xs">Not set</span>
                                )}
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <div className="flex flex-col items-center gap-2">
                                {/* Images */}
                                {imageAttachments.map((img, idx) => (
                                  <a key={idx} href={img.url} target="_blank" rel="noopener noreferrer">
                                    <img src={img.url} alt="attachment" style={{ maxWidth: 60, maxHeight: 60, borderRadius: 8 }} />
                                  </a>
                                ))}
                                {/* Files */}
                                {fileAttachments.map((file, idx) => (
                                  <a key={idx} href={file.url} target="_blank" rel="noopener noreferrer" download style={{ display: 'block', marginTop: 4 }}>
                                    Download
                                  </a>
                                ))}
                                {/* Audio */}
                                {audioAttachments.map((audio, idx) => (
                                  <audio key={idx} controls src={audio.url} style={{ width: 120, marginTop: 4 }} />
                                ))}
                                {(imageAttachments.length === 0 && fileAttachments.length === 0 && audioAttachments.length === 0) && (
                                  <span className="text-gray-400 text-xs">-</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <svg className="w-20 h-20 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <h4 className="text-lg font-medium text-gray-900 mb-2" style={{ color: '#2C2C2C' }}>No Status History</h4>
                  <p className="text-gray-500 mb-4" style={{ color: '#6E6E6E' }}>No status updates have been recorded for this task yet.</p>
                </div>
              )}
            </div>

            {statusHistory.length > 0 && (
              <div className="mt-4 text-sm text-gray-600 text-center" style={{ color: '#6E6E6E' }}>
                Showing {statusHistory.length} status update{statusHistory.length !== 1 ? 's' : ''} (newest first)
              </div>
            )}
          </div>
        </div>

        {/* Hidden audio element for playback */}
        <audio ref={audioRef} style={{ display: 'none' }} />
      </div>

      {/* Snackbar Component */}
      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        type={snackbar.type}
        duration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        position="top-right"
      />

      {/* Confirm Dialog Component */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
        loading={dialogLoading}
        showIcon={true}
      />
    </div>
  );
};

export default UpdateStatus;

